<template>
    <div class="glass-container">
        <div class="policies-management-container">
            <!-- Main Card -->
            <div class="modern-card">
                <!-- Header Section -->
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Invoice Policies</h1>
                        <p class="page-subtitle">Manage policy text displayed on customer invoices</p>
                    </div>
                    
                    <div class="action-buttons">
                        <b-button @click="save_policy_items" variant="success" class="action-btn save-btn" :disabled="!isValid">
                            <b-icon icon="save" class="mr-2"></b-icon>
                            SAVE
                        </b-button>
                        <b-button @click="fetch_init" variant="outline-secondary" class="action-btn reload-btn">
                            <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                            RELOAD
                        </b-button>
                    </div>
                </div>

                <!-- Language Selector - Simple and Clean -->
              <!-- Language Selector - Simple and Clean -->
                <div class="language-selector" style="margin-bottom: 20px; display: flex; align-items: center; gap: 15px; flex-wrap: wrap;">
                    <div style="display: flex; align-items: center; gap: 10px;">
                        <span style="font-size: 14px; font-weight: 600; color: #2d3748;">Language:</span>
                        <div class="language-buttons">
                            <b-button 
                                @click="switchLanguage('en')"
                                :variant="currentLanguage === 'en' ? 'primary' : 'outline-primary'"
                                size="sm"
                                class="language-btn"
                            >
                                🇺🇸 ENGLISH
                            </b-button>
                            <b-button 
                                @click="switchLanguage('es')"
                                :variant="currentLanguage === 'es' ? 'primary' : 'outline-primary'"
                                size="sm"
                                class="language-btn"
                            >
                                🇪🇸 SPANISH
                            </b-button>
                        </div>
                    </div>
                    <div style="margin-left: auto; display: flex; gap: 10px;">
                        <b-badge v-if="hasEnglishPolicy" variant="success" style="padding: 5px 10px;">EN ✓</b-badge>
                        <b-badge v-if="hasSpanishPolicy" variant="success" style="padding: 5px 10px;">ES ✓</b-badge>
                    </div>
                </div>

                <!-- Warning Alert -->
                <b-alert variant="warning" show class="warning-alert">
                    <div class="alert-content">
                        <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                        <div>
                            <strong>Important:</strong> This tool contains policy text to be shown on customer invoices. 
                            Please update with caution.
                        </div>
                    </div>
                </b-alert>

                <!-- Last Updated Info -->
                <b-alert variant="info" show class="info-alert" v-if="ts">
                    <div class="alert-content">
                        <b-icon icon="clock-history" class="mr-2"></b-icon>
                        <div>
                            <strong>Last Updated:</strong> {{ ts }} 
                            <span style="margin-left: 10px; color: #0d6efd; font-weight: 500;">({{ currentLanguage === 'en' ? 'English' : 'Spanish' }})</span>
                        </div>
                    </div>
                </b-alert>

                <!-- Loading State -->
                <div v-if="items_being_loaded" class="loading-container">
                    <loading 
                        :active="items_being_loaded" 
                        :can-cancel="false" 
                        :is-full-page="false"
                    ></loading>
                </div>

                <!-- Policy Editor -->
                <div v-else class="policy-editor-container">
                    <div class="editor-header">
                        <h6 class="editor-title">
                            {{ currentLanguage === 'en' ? 'ENGLISH POLICY' : 'SPANISH POLICY' }}
                        </h6>
                        <div class="editor-info">
                            <span class="char-count">
                                {{ text ? text.length : 0 }} characters
                            </span>
                        </div>
                    </div>
                    
                    <div class="editor-wrapper">
                        <!-- WYSIWYG Editor -->
                        <wysiwyg 
                            v-model="text" 
                            class="policy-wysiwyg-editor"
                            :editor-class="'wysiwyg-custom'"
                            :toolbar-class="'wysiwyg-toolbar-custom'"
                        />
                    </div>
                    
                    <!-- Action Buttons Footer - Simple and Clean -->
                    <div class="editor-footer">
                        <div style="display: flex; align-items: center; gap: 15px;">
                            <b-badge v-if="currentLanguage === 'en' && hasEnglishPolicy" variant="success" style="padding: 5px 10px;">✓ English saved</b-badge>
                            <b-badge v-else-if="currentLanguage === 'en'" variant="light" style="padding: 5px 10px; color: #999;">English not saved</b-badge>
                            <b-badge v-if="currentLanguage === 'es' && hasSpanishPolicy" variant="success" style="padding: 5px 10px;">✓ Spanish saved</b-badge>
                            <b-badge v-else-if="currentLanguage === 'es'" variant="light" style="padding: 5px 10px; color: #999;">Spanish not saved</b-badge>
                        </div>
                        <div class="action-buttons-footer">
                            <b-button @click="fetch_init" variant="outline-secondary" size="sm" class="action-btn">
                                <b-icon icon="x" class="mr-1"></b-icon>
                                RELOAD
                            </b-button>
                            <b-button 
                                @click="save_policy_items" 
                                variant="success" 
                                size="sm" 
                                class="action-btn"
                                :disabled="!isValid"
                            >
                                <b-icon icon="check" class="mr-1"></b-icon>
                                SAVE
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Backer from '@/components/globals/Backer'
    import Requestor from '@/utilities/Requestor'
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        name: "Policies",
        components: { Backer, Loading },
        data: function(){
            return {
                packet: {},
                items_being_loaded: null,
                text: null,
                ts: null,
                id: null,
                currentLanguage: 'en',
                policies: {
                    en: { text: null, ts: null, id: null },
                    es: { text: null, ts: null, id: null }
                }
            }
        },
        computed: {
            hasEnglishPolicy() {
                return !!this.policies.en.text;
            },
            hasSpanishPolicy() {
                return !!this.policies.es.text;
            },
            isValid() {
                return this.text !== null && this.text !== '';
            }
        },
        methods: {
          switchLanguage(lang) {
    if (this.currentLanguage === lang) return; // Already in this language
    
    // Save current language's text before switching
    if (this.text) {
        this.policies[this.currentLanguage].text = this.text;
        this.policies[this.currentLanguage].ts = this.ts;
        this.policies[this.currentLanguage].id = this.id;
    }
    
    // Switch to new language
    this.currentLanguage = lang;
    
    // Load the new language's text
    if (this.policies[lang].text) {
        this.text = this.policies[lang].text;
        this.ts = this.policies[lang].ts;
        this.id = this.policies[lang].id;
    } else {
        this.fetchPolicy(lang);
    }
},
            
            fetchPolicy(language) {
                let path = "/api/policy/get_policy/"
                let data = { language: language }
                let that = this;
                
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant: "warning"}
                        );
                    }
                    else {
                        if (response.data.result && response.data.result.length > 0){
                            const policy = response.data.result[0];
                            that.policies[language] = {
                                text: policy.text,
                                ts: policy.ts,
                                id: policy.id
                            };
                            
                            if (that.currentLanguage === language) {
                                that.text = policy.text;
                                that.ts = policy.ts;
                                that.id = policy.id;
                            }
                        } else {
                            that.policies[language] = { text: null, ts: null, id: null };
                            if (that.currentLanguage === language) {
                                that.text = null;
                                that.ts = null;
                                that.id = null;
                            }
                        }
                    }
                    that.items_being_loaded = false;
                }
                
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        `Error loading ${language === 'en' ? 'English' : 'Spanish'} policy text. Please refresh.`, 
                        {title: "Error", autoHideDelay: 1500, variant: "warning"}
                    );
                    that.items_being_loaded = false;
                }
                
                this.items_being_loaded = true;
                Requestor.getSyncP(path, data, this, s, f);
            },
            
            save_policy_items: function(){
                if (!this.isValid) {
                    this.$bvToast.toast(
                        "Please enter policy text before saving.", 
                        {title: "Validation Error", autoHideDelay: 1500, variant: "warning"}
                    );
                    return;
                }
                
                let path = "/api/policy/save_policy/"
                let textToSave = this.text ? this.text.toString().trim() : "";
                
                let data = {
                    text: textToSave, 
                    id: this.id,
                    language: this.currentLanguage
                }
                
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.policies[that.currentLanguage].text = textToSave;
                        that.policies[that.currentLanguage].ts = new Date().toLocaleString();
                        that.policies[that.currentLanguage].id = response.data.result[0].id;
                        
                        that.fetch_init();
                        
                        that.$bvToast.toast(
                            `${that.currentLanguage === 'en' ? 'English' : 'Spanish'} policy updated successfully.`, 
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error updating policy. Please try again.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true;
                Requestor.postSyncP(path, data, this, s, f);
            },
            
            fetch_init: function(){
                this.fetchPolicy('en');
                this.fetchPolicy('es');
            }
        },
        created: function(){
            if (localStorage.packet) {
                this.packet = JSON.parse(localStorage.packet);
            }
            if (localStorage.userDetails) {
                this.packet.userDetails = JSON.parse(localStorage.userDetails);
            }
            
            const savedLanguage = localStorage.getItem('policyManagementLanguage');
            if (savedLanguage) {
                this.currentLanguage = savedLanguage;
            }
            
            this.fetch_init();
        },
        watch: {
            currentLanguage(newVal) {
                localStorage.setItem('policyManagementLanguage', newVal);
            }
        }
    }
</script>

<style scoped>
/* Your existing styles remain exactly the same */
.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 0;
}

.policies-management-container {
    padding: 20px;
    background: transparent;
    min-height: 100vh;
    border-radius: 34px;
    z-index: 1;
    position: relative;
}

.modern-card {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    padding: 25px;
    margin: 0 auto;
    margin-top: 10px;
    max-width: 1200px;
}

/* Header */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 2px solid #f0f0f0;
}

.title-section .page-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
}

.title-section .page-subtitle {
    color: #718096;
    font-size: 0.95rem;
    margin: 5px 0 0;
}

.action-buttons {
    display: flex;
    gap: 10px;
}

.action-btn {
    padding: 8px 16px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.save-btn {
    background: #48bb78;
    border: none;
}

.save-btn:hover {
    background: #38a169;
    transform: translateY(-1px);
}

.reload-btn:hover {
    transform: translateY(-1px);
}

/* Alerts */
.warning-alert {
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.2);
    border-radius: 8px;
    margin-bottom: 15px;
}

.info-alert {
    background: rgba(13, 110, 253, 0.1);
    border: 1px solid rgba(13, 110, 253, 0.2);
    border-radius: 8px;
    margin-bottom: 25px;
}

.alert-content {
    display: flex;
    align-items: flex-start;
}

.warning-alert .bi {
    color: #f59e0b;
    margin-top: 2px;
}

.info-alert .bi {
    color: #0d6efd;
    margin-top: 2px;
}

/* Loading */
.loading-container {
    text-align: center;
    padding: 40px 20px;
}

/* Policy Editor */
.policy-editor-container {
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 20px;
    border: 1px solid #e2e8f0;
}

.editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e2e8f0;
}

.editor-title {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.editor-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.char-count {
    font-size: 12px;
    color: #718096;
    font-weight: 500;
    background: #f8f9fa;
    padding: 3px 8px;
    border-radius: 4px;
}

/* WYSIWYG Editor Styling */
.editor-wrapper {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    background: white;
}

.policy-wysiwyg-editor {
    max-height: 500px;
    overflow-y: auto;
}

/* Custom WYSIWYG styles */
.policy-wysiwyg-editor >>> .wysiwyg-toolbar-custom {
    background: #f8fafc !important;
    border-bottom: 1px solid #e2e8f0 !important;
    padding: 10px 15px !important;
    border-radius: 0 !important;
}

.policy-wysiwyg-editor >>> .wysiwyg-toolbar-custom .btn {
    background: white !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 4px !important;
    color: #4a5568 !important;
    margin: 0 2px !important;
    padding: 6px 10px !important;
    transition: all 0.2s ease !important;
}

.policy-wysiwyg-editor >>> .wysiwyg-toolbar-custom .btn:hover {
    background: #edf2f7 !important;
    border-color: #cbd5e0 !important;
    color: #2d3748 !important;
}

.policy-wysiwyg-editor >>> .wysiwyg-custom {
    min-height: 300px !important;
    max-height: 400px !important;
    overflow-y: auto !important;
    padding: 15px !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
    color: #2d3748 !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
    border: none !important;
    border-radius: 0 !important;
}

.policy-wysiwyg-editor >>> .wysiwyg-custom:focus {
    outline: none !important;
    box-shadow: none !important;
}

.policy-wysiwyg-editor >>> .wysiwyg-custom p {
    margin-bottom: 10px !important;
}

.policy-wysiwyg-editor >>> .wysiwyg-custom ul,
.policy-wysiwyg-editor >>> .wysiwyg-custom ol {
    margin-left: 20px !important;
    margin-bottom: 10px !important;
}

.policy-wysiwyg-editor >>> .wysiwyg-custom li {
    margin-bottom: 5px !important;
}

/* Editor Footer */
.editor-footer {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.action-buttons-footer {
    display: flex;
    gap: 10px;
}

/* Responsive Design */
@media (max-width: 768px) {
    .modern-card {
        padding: 15px;
    }
    
    .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .action-buttons {
        width: 100%;
    }
    
    .action-btn {
        flex: 1;
        justify-content: center;
    }
    
    .editor-header {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }
    
    .editor-footer {
        flex-direction: column;
    }
    
    .action-buttons-footer {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .action-buttons {
        flex-direction: column;
    }
    
    .action-btn {
        width: 100%;
        justify-content: center;
    }
    
    .language-selector {
        flex-direction: column;
        align-items: flex-start !important;
    }
    
    .language-selector > div:last-child {
        margin-left: 0 !important;
    }
}
.language-buttons {
    display: flex;
    gap: 5px;
}

.language-btn {
    min-width: 100px;
    font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/components/ControlPanel/Policies.vue